<script setup>
import tomato from '~/design-system/assets/logo.png';

const plants = ['pepper', 'strawberry', 'eggplant', 'blueberry', 'cucumber', 'pumpkin', 'dahlia', 'raspberry', 'cherry', 'watermelon', 'blackcurrant', 'apple', 'pear', 'plum', 'grape', 'sunflower', 'rose', 'peony', 'tulip', 'lily', 'iris', 'hydrangea', 'marigold', 'cosmos', 'lavender'];
const current = ref('tomato');
const source = ref(tomato);
let timer;
let idle;
let motion;
let connection;
let disposed = false;
let busy = false;
let count = 0;
let bag = [];
const failed = new Set();

const paused = () => disposed || document.hidden || motion?.matches || connection?.saveData;
function cancel() {
  clearTimeout(timer);
  if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
  else clearTimeout(idle);
}
function choose() {
  if (count >= 4) return 'tomato';
  if (!bag.length) bag = plants.filter(plant => !failed.has(plant));
  const choices = bag.filter(plant => plant !== current.value);
  if (!choices.length) return 'tomato';
  const plant = choices[Math.floor(Math.random() * choices.length)];
  bag.splice(bag.indexOf(plant), 1);
  return plant;
}
function schedule() {
  cancel();
  if (paused() || busy || failed.size === plants.length) return;
  timer = setTimeout(() => {
    const advance = async () => {
      if (paused()) return;
      const plant = choose();
      const url = plant === 'tomato' ? tomato : `/images/plant-logo/${plant}.webp`;
      busy = true;
      try {
        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.src = url;
        await image.decode();
        if (!paused()) {
          current.value = plant;
          source.value = url;
          count = plant === 'tomato' ? 0 : count + 1;
        }
      } catch { if (plant !== 'tomato') failed.add(plant); }
      finally { busy = false; schedule(); }
    };
    idle = window.requestIdleCallback ? window.requestIdleCallback(advance, { timeout: 2000 }) : setTimeout(advance, 100);
  }, current.value === 'tomato' ? 10000 : 5000);
}
function reset() {
  if (motion?.matches || connection?.saveData) {
    current.value = 'tomato'; source.value = tomato; count = 0;
  }
  schedule();
}
onMounted(() => {
  motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  connection = navigator.connection;
  motion.addEventListener('change', reset);
  connection?.addEventListener('change', reset);
  document.addEventListener('visibilitychange', reset);
  schedule();
});
onBeforeUnmount(() => {
  disposed = true;
  cancel();
  motion?.removeEventListener('change', reset);
  connection?.removeEventListener('change', reset);
  document.removeEventListener('visibilitychange', reset);
});
</script>

<template>
  <span class="uo-plant-logo" aria-hidden="true" :data-plant="current">
    <Transition name="uo-logo-dissolve">
      <img :key="source" :src="source" width="32" height="32" alt="" decoding="async" />
    </Transition>
  </span>
</template>

<style scoped>
.uo-plant-logo { position: relative; display: inline-block; width: 32px; height: 32px; flex-shrink: 0; border-radius: 50%; overflow: hidden; }
.uo-plant-logo img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: cover; }
.uo-logo-dissolve-enter-active { z-index: 1; transition: opacity .5s linear; }
.uo-logo-dissolve-leave-active { transition: opacity .5s linear; }
.uo-logo-dissolve-enter-from { opacity: 0; }
.uo-logo-dissolve-leave-to { opacity: 1; }
@media (prefers-reduced-motion: reduce) { .uo-plant-logo img { transition: none; } }
</style>